import * as React from 'react';
import { cn } from '../../lib/cn';
import type { PolymorphicComponentProps, SurfaceVariant } from '../../lib/types';

export const surfaceVariantClasses = {
  default: 'bg-surface-elevated border border-border-default',
  elevated: 'bg-surface-elevated shadow-md border border-border-subtle',
  sunken: 'bg-surface-sunken border border-border-muted',
  overlay: 'bg-surface-overlay/95 backdrop-blur-sm text-text-inverse border border-white/15',
} satisfies Record<SurfaceVariant, string>;

export const surfaceRadiusClasses = {
  none: 'rounded-none',
  sm: 'rounded-sm',
  md: 'rounded-md',
  lg: 'rounded-lg',
  xl: 'rounded-xl',
  '2xl': 'rounded-2xl',
  full: 'rounded-full',
} as const;

type SurfaceRadius = keyof typeof surfaceRadiusClasses;

/**
 * Shadow depth options, overriding the variant's own shadow.
 */
export type SurfaceElevation = 'none' | 'sm' | 'md' | 'lg';

export const surfaceElevationClasses = {
  none: 'shadow-none',
  sm: 'shadow-sm',
  md: 'shadow-md',
  lg: 'shadow-lg',
} satisfies Record<SurfaceElevation, string>;

const interactiveClasses =
  'hover:border-primary/60 focus-visible:ring-ring transition-shadow hover:shadow-lg focus-visible:ring-2 focus-visible:outline-none disabled:opacity-60';

type FocusableSurfaceElement = 'button' | 'a';

interface SurfaceOwnProps<T extends React.ElementType> {
  /** @default 'default' */
  variant?: SurfaceVariant;
  /** Border radius. @default '2xl' */
  radius?: SurfaceRadius;
  /** Defaults to the shadow the variant already carries. */
  elevation?: SurfaceElevation;
  /**
   * Adds hover, focus and disabled affordances for a clickable surface. Only
   * `as="button"` or `as="a"` accept it, because a `div` cannot take focus.
   * @default false
   */
  interactive?: T extends FocusableSurfaceElement ? boolean : false;
  className?: string;
}

export type SurfaceProps<T extends React.ElementType = 'div'> = PolymorphicComponentProps<
  T,
  SurfaceOwnProps<T>
>;

export function Surface<T extends React.ElementType = 'div'>({
  as,
  variant = 'default',
  radius = '2xl',
  elevation,
  interactive = false,
  className,
  ref,
  ...props
}: SurfaceProps<T>) {
  const Component = as ?? 'div';

  return (
    <Component
      ref={ref}
      className={cn(
        surfaceVariantClasses[variant],
        surfaceRadiusClasses[radius],
        elevation && surfaceElevationClasses[elevation],
        interactive && interactiveClasses,
        className,
      )}
      data-variant={variant}
      {...props}
    />
  );
}
